"use client";

import {
  useAddWishlist,
  useDeleteWishlist,
  useWishlist,
} from "@/data/wishlist";
import { useEffect, useState } from "react";
import { signIn } from "next-auth/react";
import { Session } from "next-auth";
import Dialog from "@repo/ui/components/ui/dialog";
import { Button } from "@repo/ui/components/ui/button";
import { usePathname } from "next/navigation";
import { useCurrentLocale } from "@repo/next-shared/hooks/useCurrentLocale";
import { useTranslations } from "next-intl";
import { ProductDetails, Variant } from "@/types/product-details";
import { Product } from "@/types/product-search";
import { useCategory } from "@/data/categories";
import { pushEcommerceEvent } from "@/lib/data-layer";
import { cn } from "@repo/ui/lib/utils";
import Icon from "@repo/ui/components/ui/icon/icon";
import { Skeleton } from "@repo/ui/components/ui/skeleton";

export default function Wishlist({
  product,
  session,
  className = "",
}: {
  product: ProductDetails;
  session: Session | null;
  className?: string;
}) {
  const pathname = usePathname();
  const { data: wishlist, isLoading } = useWishlist();
  const [inWishlist, setInWishlist] = useState(false);
  const [loginDialogOpen, setLoginDialogOpen] = useState(false);
  const addToWishlist = useAddWishlist(product.id);
  const removeFromWishlist = useDeleteWishlist(product.id);
  const ignoreRoutes = ["/login", "/logout"];
  const locale = useCurrentLocale();
  const t = useTranslations("Product");
  const { data: category } = useCategory(product?.categoryId ?? "");

  function onAddToFavorites() {
    if (session) {
      addToWishlist.mutate();
      if (product) {
        pushEcommerceEvent({
          event: "add_to_wishlist",
          ecommerce: {
            currency: "EUR",
            value: product.minVariantTargetPrice,
            items: product.variants.map((variant: Variant) => ({
              item_id: product.id,
              item_name: product.title,
              item_brand: product.brand,
              item_category: category?.title,
              item_variant: `${variant.color.name},${variant.size}`,
              quantity: 1,
              price: variant.targetPrice,
            })),
          },
        });
      }
    } else {
      setLoginDialogOpen(true);
    }
  }

  function removeFromFavorites() {
    removeFromWishlist.mutate();
  }

  const loginDialog = (
    <Dialog
      open={loginDialogOpen}
      setOpen={setLoginDialogOpen}
      header={t("sign-in-or-register")}
    >
      <p className="mt-10 text-primary-black">
        {t("add-to-whishlist-description")}
      </p>
      <div className="flex flex-col gap-5 mt-8">
        <Button
          onClick={() =>
            signIn(
              "keycloak",
              {
                ...(ignoreRoutes.includes(pathname) && { redirectTo: "/" }),
              },
              {
                kc_locale: locale,
                ui_locales: locale,
              },
            )
          }
          variant="no-dark"
        >
          {t("sign-in")}
        </Button>
        <Button
          onClick={() =>
            signIn(
              "keycloak",
              {
                ...(ignoreRoutes.includes(pathname) && { redirectTo: "/" }),
              },
              {
                kc_locale: locale,
                ui_locales: locale,
                kc_action: "register",
              },
            )
          }
          variant="outline"
        >
          {t("register")}
        </Button>
      </div>
    </Dialog>
  );

  useEffect(() => {
    if (wishlist && typeof wishlist === "object" && wishlist.length > 0) {
      setInWishlist(!!wishlist.find((item: any) => item.id === product.id));
    } else {
      setInWishlist(false);
    }
  }, [wishlist]);

  if (isLoading) {
    return (
      <Skeleton
        className={cn(
          "w-[46px] h-[46px] rounded-full dark:bg-secondary-gray1",
          {
            "rounded-[10px]": !!className,
          },
        )}
      />
    );
  }

  return (
    <>
      <button
        aria-label="add to wishlist"
        onClick={inWishlist ? removeFromFavorites : onAddToFavorites}
      >
        <Icon
          icon="Heart"
          className={cn(
            "max-md:light w-[46px] h-[46px] max-md:dark:bg-primary-magenta/20 bg-primary-magenta/20 dark:bg-primary-white rounded-full flex items-center justify-center *:w-[28px] *:h-[20px] *:mt-[2px] stroke-primary-magenta fill-none md:hover:fill-primary-magenta",
            className,
            { "fill-primary-magenta": inWishlist },
            { invisible: !wishlist },
          )}
        ></Icon>
      </button>
      {loginDialog}
    </>
  );
}
